import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  LayoutDashboard,
  FileText,
  Calendar as CalendarIcon,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  Plus,
  Send,
  Eye,
  Award,
  Building,
  Clock,
  IndianRupee,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RFPModule = () => {
  const { consortiumId } = useParams();
  const [rfps, setRfps] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deadline, setDeadline] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    requirements: "",
    budget_range: "",
  });

  const sidebarItems = [
    {
      section: "Consortium",
      items: [
        { name: "Overview", path: `/consortium/${consortiumId}`, icon: LayoutDashboard },
        { name: "Agreements", path: `/consortium/${consortiumId}/agreements`, icon: FileText },
        { name: "Meetings", path: `/consortium/${consortiumId}/meetings`, icon: CalendarIcon },
        { name: "Voting", path: `/consortium/${consortiumId}/voting`, icon: Vote },
        { name: "RFPs & Bids", path: `/consortium/${consortiumId}/rfps`, icon: FileSpreadsheet },
        { name: "Consent", path: `/consortium/${consortiumId}/consent`, icon: CheckSquare },
      ],
    },
  ];

  useEffect(() => {
    fetchData();
  }, [consortiumId]);

  const fetchData = async () => {
    try {
      const [rfpsRes, bidsRes] = await Promise.all([
        axios.get(`${API}/rfps?consortium_id=${consortiumId}`),
        axios.get(`${API}/bids`),
      ]);
      setRfps(rfpsRes.data);
      setBids(bidsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRFP = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/rfps`, {
        consortium_id: consortiumId,
        title: formData.title,
        description: formData.description,
        requirements: formData.requirements.split("\n").filter((r) => r.trim()),
        submission_deadline: deadline.toISOString(),
        budget_range: formData.budget_range,
        evaluation_criteria: {
          price: 40,
          experience: 30,
          timeline: 20,
          approach: 10,
        },
      });
      toast.success("RFP created successfully!");
      setDialogOpen(false);
      setFormData({ title: "", description: "", requirements: "", budget_range: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create RFP");
    }
  };

  const handlePublishRFP = async (rfpId) => {
    try {
      await axios.put(`${API}/rfps/${rfpId}/publish`);
      toast.success("RFP published!");
      fetchData();
    } catch (error) {
      toast.error("Failed to publish RFP");
    }
  };

  const handleSelectBid = async (bidId) => {
    try {
      await axios.put(`${API}/bids/${bidId}/select`);
      toast.success("Bid selected and awarded!");
      fetchData();
    } catch (error) {
      toast.error("Failed to select bid");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-slate-100 text-slate-600";
      case "published":
        return "bg-blue-100 text-[#4E80FF]";
      case "closed":
        return "bg-yellow-100 text-yellow-600";
      case "awarded":
        return "bg-blue-100 text-[#4E80FF]";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <div className="space-y-6" data-testid="rfp-module">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              RFPs & Bid Management
            </h1>
            <p className="text-slate-600">
              Create RFPs and manage developer bids
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-rfp-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create RFP
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Request for Proposal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateRFP} className="space-y-4">
                <div className="space-y-2">
                  <Label>RFP Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g., Township Development Project"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Project Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Detailed description of the development project"
                    rows={4}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Requirements (one per line)</Label>
                  <Textarea
                    value={formData.requirements}
                    onChange={(e) =>
                      setFormData({ ...formData, requirements: e.target.value })
                    }
                    placeholder="Minimum 10 years experience&#10;RERA registered&#10;Financial capacity of 100 Cr+"
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget Range</Label>
                    <Input
                      value={formData.budget_range}
                      onChange={(e) =>
                        setFormData({ ...formData, budget_range: e.target.value })
                      }
                      placeholder="e.g., 50-100 Cr"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Submission Deadline</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {format(deadline, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={deadline}
                          onSelect={(date) => date && setDeadline(date)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Create RFP
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="rfps">
          <TabsList>
            <TabsTrigger value="rfps">RFPs</TabsTrigger>
            <TabsTrigger value="bids">Bids Received</TabsTrigger>
          </TabsList>

          <TabsContent value="rfps" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
              </div>
            ) : rfps.length > 0 ? (
              <div className="space-y-4">
                {rfps.map((rfp) => (
                  <Card key={rfp.id} className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileSpreadsheet className="w-6 h-6 text-[#4E80FF]" />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-semibold text-slate-900">
                              {rfp.title}
                            </h3>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                              {rfp.description}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                Deadline: {new Date(rfp.submission_deadline).toLocaleDateString()}
                              </span>
                              {rfp.budget_range && (
                                <span className="flex items-center gap-1">
                                  <IndianRupee className="w-4 h-4" />
                                  {rfp.budget_range}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Badge className={getStatusColor(rfp.status)}>
                          {rfp.status}
                        </Badge>
                      </div>

                      {rfp.requirements?.length > 0 && (
                        <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium text-slate-700 mb-2">Requirements:</p>
                          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                            {rfp.requirements.slice(0, 3).map((req, idx) => (
                              <li key={idx}>{req}</li>
                            ))}
                            {rfp.requirements.length > 3 && (
                              <li className="text-slate-400">
                                +{rfp.requirements.length - 3} more
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-slate-500">
                          {rfp.invited_developers?.length || 0} developers invited
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {rfp.status === "draft" && (
                            <Button
                              size="sm"
                              className="btn-secondary"
                              onClick={() => handlePublishRFP(rfp.id)}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Publish
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center">
                  <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    No RFPs Created
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Create an RFP to invite developers for your project.
                  </p>
                  <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                    Create RFP
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="bids" className="mt-6">
            {bids.length > 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Developer</TableHead>
                        <TableHead>Proposal</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Timeline</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bids.map((bid) => (
                        <TableRow key={bid.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{bid.developer_id?.substring(0, 8)}...</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {bid.proposal}
                          </TableCell>
                          <TableCell>₹{(bid.amount / 10000000).toFixed(1)} Cr</TableCell>
                          <TableCell>{bid.timeline_months} months</TableCell>
                          <TableCell>
                            {bid.score ? (
                              <span className="font-medium">{bid.score}/100</span>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                bid.status === "selected"
                                  ? "bg-[#4E80FF]"
                                  : bid.status === "rejected"
                                  ? "bg-red-500"
                                  : ""
                              }
                              variant={
                                bid.status === "submitted" ? "outline" : "default"
                              }
                            >
                              {bid.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                              {bid.status === "submitted" && (
                                <Button
                                  size="sm"
                                  className="btn-primary"
                                  onClick={() => handleSelectBid(bid.id)}
                                >
                                  <Award className="w-4 h-4 mr-1" />
                                  Select
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center">
                  <Building className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    No Bids Received
                  </h3>
                  <p className="text-slate-500">
                    Publish an RFP to start receiving bids from developers.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default RFPModule;
